import { getPartnerLogo } from '../../assets/partners/logos';
import type { IconName } from '../../components/ui/icon/types';
import { features } from '../../constants/features';
import type { I18nPrefix } from '../../i18n/prefix';
import { BrightpearlModal } from './components/modals/brightpearl-modal';
import { Cin7Modal } from './components/modals/cin7-modal';
import { IntuitiveShippingModal } from './components/modals/intuitive-shipping-modal';
import { KatanaModal } from './components/modals/katana-modal';
import { LinnworksModal } from './components/modals/linnworks-modal';
import { QuickBooksModal } from './components/modals/quickbooks-modal';
import { SufioModal } from './components/modals/sufio-modal';
import { UnleashedModal } from './components/modals/unleashed-modal';
import { XeroModal } from './components/modals/xero-modal';
import type { Partner, PartnerGroupType } from './types';

const PAGE = 'features:partners.pages.partners_page' satisfies I18nPrefix;
const OAUTH_CALLBACK = 'features:partners.pages.oauth_callback' satisfies I18nPrefix;

export const PARTNERS_I18N = {
  page: PAGE,
  oauthCallback: OAUTH_CALLBACK,
} as const;

export const PARTNER_DOCS_URLS = Object.freeze({
  learnMore: 'https://docs.sparklayer.io/integrations',
  linnworks: 'https://docs.sparklayer.io/linnworks',
  brightpearl: 'https://docs.sparklayer.io/brightpearl',
  unleashed: 'https://docs.sparklayer.io/unleashed',
  cin7: 'https://docs.sparklayer.io/cin7-core',
  katana: 'https://docs.sparklayer.io/katana',
  xero: 'https://docs.sparklayer.io/xero',
  quickbooks: 'https://docs.sparklayer.io/quickbooks',
  sufio: 'https://docs.sparklayer.io/sufio-invoicing',
  intuitiveShipping: 'https://docs.sparklayer.io/intuitive-shipping',
  erps: 'https://docs.sparklayer.io/erps-and-operations',
});

export const PARTNERS: Partner[] = [
  {
    id: 'linnworks',
    settingKey: 'integrationLinnworks',
    logo: getPartnerLogo('linnworks'),
    name: 'Linnworks',
    description: `${PAGE}.partners.linnworks.description`,
    docsUrl: PARTNER_DOCS_URLS.linnworks,
    isAdvancedOnly: true,
    sync: ['pricing'],
    Modal: LinnworksModal,
  },
  {
    id: 'brightpearl',
    settingKey: 'integrationBrightpearl',
    logo: getPartnerLogo('brightpearl'),
    name: 'Brightpearl',
    description: `${PAGE}.partners.brightpearl.description`,
    docsUrl: PARTNER_DOCS_URLS.brightpearl,
    isAdvancedOnly: true,
    sync: ['pricing', 'customers'],
    Modal: BrightpearlModal,
  },
  {
    id: 'unleashed',
    settingKey: 'integrationUnleashed',
    logo: getPartnerLogo('unleashed'),
    name: 'Unleashed',
    description: `${PAGE}.partners.unleashed.description`,
    docsUrl: PARTNER_DOCS_URLS.unleashed,
    isAdvancedOnly: true,
    sync: ['pricing', 'customers'],
    Modal: UnleashedModal,
  },
  {
    id: 'cin7',
    settingKey: 'integrationCin7',
    logo: getPartnerLogo('cin7'),
    name: 'Cin7 Core',
    description: `${PAGE}.partners.cin7.description`,
    docsUrl: PARTNER_DOCS_URLS.cin7,
    isAdvancedOnly: true,
    sync: ['pricing', 'customers'],
    Modal: Cin7Modal,
  },
  {
    id: 'katana',
    settingKey: 'integrationKatana',
    logo: getPartnerLogo('katana'),
    name: 'Katana',
    description: `${PAGE}.partners.katana.description`,
    docsUrl: PARTNER_DOCS_URLS.katana,
    isAdvancedOnly: true,
    sync: ['customers'],
    Modal: KatanaModal,
  },
  {
    id: 'xero',
    settingKey: 'integrationXero',
    logo: getPartnerLogo('xero'),
    name: 'Xero',
    description: `${PAGE}.partners.xero.description`,
    docsUrl: PARTNER_DOCS_URLS.xero,
    isAdvancedOnly: true,
    feature: features.XERO,
    Modal: XeroModal,
  },
  {
    id: 'quickBooks',
    settingKey: 'integrationQuickBooks',
    logo: getPartnerLogo('quickbooks'),
    name: 'QuickBooks',
    description: `${PAGE}.partners.quickbooks.description`,
    docsUrl: PARTNER_DOCS_URLS.quickbooks,
    isAdvancedOnly: true,
    feature: features.QUICKBOOKS,
    Modal: QuickBooksModal,
  },
  {
    id: 'sufio',
    settingKey: 'integrationSufio',
    logo: getPartnerLogo('sufio'),
    name: 'Sufio',
    description: `${PAGE}.partners.sufio.description`,
    docsUrl: PARTNER_DOCS_URLS.sufio,
    isAdvancedOnly: false,
    Modal: SufioModal,
  },
  {
    id: 'intuitiveShipping',
    settingKey: 'integrationIntuitiveShipping',
    logo: getPartnerLogo('intuitive-shipping'),
    name: 'Intuitive Shipping',
    description: `${PAGE}.partners.intuitive_shipping.description`,
    docsUrl: PARTNER_DOCS_URLS.intuitiveShipping,
    isAdvancedOnly: false,
    Modal: IntuitiveShippingModal,
  },
];

export const GROUP_ICONS: Record<PartnerGroupType, IconName> = {
  orders: 'squaresPlus',
  accountancy: 'buildingOffice',
  invoicing: 'document',
  shipping: 'truck',
};

type PartnerGroupDefinition = {
  type: PartnerGroupType;
  partnerIds: string[];
};

export const PARTNER_GROUPS: PartnerGroupDefinition[] = [
  {
    type: 'orders',
    partnerIds: ['linnworks', 'brightpearl', 'unleashed', 'cin7', 'katana'],
  },
  {
    type: 'accountancy',
    partnerIds: ['xero', 'quickBooks'],
  },
  {
    type: 'invoicing',
    partnerIds: ['sufio'],
  },
  {
    type: 'shipping',
    partnerIds: ['intuitiveShipping'],
  },
];
